/**
 * TrendTooltip — the trust envelope for one trend point: Actual (RAG-coloured) vs
 * Target formatted exactly as the grid (formatCellValue), plus lock state, freshness
 * and any DQ flag. Rendered as Recharts' Tooltip content; reads the TrendPoint off
 * payload[0].payload. Typed props only (CD-7); a null value reads its honest em-dash.
 */

import React from 'react'
import { formatCellValue, EM_DASH, RAG_CLASSES } from './cellFormat'
import type { TrendPoint } from './trendSeries'

interface TrendTooltipProps {
  active?: boolean
  payload?: Array<{ payload: TrendPoint }>
}

const LOCK_WORDS: Record<string, string> = {
  live: 'Live',
  locked: 'Locked',
  divergent: 'Locked · restated since lock',
}

export default function TrendTooltip({ active, payload }: TrendTooltipProps): React.JSX.Element | null {
  if (!active || !payload || payload.length === 0) return null
  const { label, cell, targetCell } = payload[0].payload
  return (
    <div className="rounded-md border border-slate-200 bg-white px-2.5 py-2 text-xs shadow-md dark:border-slate-700 dark:bg-slate-900">
      <p className="font-semibold text-slate-700 dark:text-slate-200">{label}</p>
      <p className={cell ? RAG_CLASSES[cell.value === null ? 'none' : cell.rag] : ''}>
        Actual: {cell ? formatCellValue(cell) : EM_DASH}
      </p>
      <p className="text-slate-500 dark:text-slate-400">
        Target: {targetCell ? formatCellValue(targetCell) : EM_DASH}
      </p>
      {cell && (
        <p className="text-slate-400 dark:text-slate-500">{LOCK_WORDS[cell.lock_state]}</p>
      )}
      {cell?.dq_flags.length ? (
        <p className="mt-1 max-w-56 text-amber-600 dark:text-amber-400">{cell.dq_flags.join(' · ')}</p>
      ) : null}
    </div>
  )
}
